import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Plus } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { adminService } from '../../services/adminService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userLabel?: string;
}

interface Balance {
  total: number;
  used: number;
  available: number;
  monthly_free: number;
}

interface Tx {
  id: string;
  type: 'debit' | 'credit' | 'refund' | 'grant';
  amount: number;
  source: string;
  description?: string | null;
  balance_after?: number | null;
  created_at: string;
}

const VideoCreditsModal: React.FC<Props> = ({ isOpen, onClose, userId, userLabel }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [transactions, setTransactions] = useState<Tx[]>([]);
  const [form, setForm] = useState({ amount: '', notes: '' });

  useEffect(() => {
    if (!isOpen || !userId) return;
    setBalance(null);
    setTransactions([]);
    setForm({ amount: '', notes: '' });
    setLoading(true);
    adminService.getCompanyVideoCredits(userId)
      .then((res) => {
        if (res.success && res.data) {
          setBalance(res.data.balance);
          setTransactions(res.data.transactions || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, userId]);

  const handleGrant = async () => {
    if (!userId) return;
    const amount = parseInt(form.amount, 10);
    if (!amount || amount <= 0) {
      toast.error(t('admin.videoCreditsInvalidAmount'));
      return;
    }
    setSaving(true);
    try {
      const res = await adminService.grantVideoCredits({ user_id: userId, amount, notes: form.notes || undefined });
      if (res.success && res.data) {
        toast.success(t('admin.videoCreditsGranted', { count: amount }));
        setBalance(res.data.balance);
        setForm({ amount: '', notes: '' });
        const refreshed = await adminService.getCompanyVideoCredits(userId);
        if (refreshed.success && refreshed.data) setTransactions(refreshed.data.transactions || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('admin.videoCreditsTitle')} size="2xl" maxHeight>
      <div className="space-y-5">
        {userLabel && <p className="text-sm text-gray-600 dark:text-gray-400">{userLabel}</p>}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {balance && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.videoCreditsAvailable')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{balance.available}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.videoCreditsTotal')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{balance.total}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.videoCreditsUsed')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{balance.used}</p>
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('admin.grantVideoCreditsManual')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t('admin.videoCreditsAmount')}
                  type="number"
                  min="1"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder={t('admin.videoCreditsAmountPlaceholder') || ''}
                />
                <Input
                  label={t('admin.videoCreditsNotes')}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={t('admin.videoCreditsNotesPlaceholder') || ''}
                />
              </div>
              <div className="flex justify-end mt-3">
                <Button onClick={handleGrant} disabled={saving || !form.amount} loading={saving}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin.videoCreditsGrantBtn')}
                </Button>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('admin.videoCreditsHistory')}</h4>
              {transactions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {transactions.map(tx => (
                    <li key={tx.id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{tx.type} · {tx.source}</p>
                        {tx.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{tx.description}</p>}
                        <p className="text-xs text-gray-400 dark:text-gray-500">{new Date(tx.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                      <span className={`text-sm font-medium ${tx.type === 'debit' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        {tx.type === 'debit' ? '-' : '+'}{tx.amount}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default VideoCreditsModal;
